import type { TransformerInterface } from '@gfe/core/models/entities/entity-visualization-interface';
import { isCsvCompatible, isInputOnly } from '@gfe/core/utils/json-schema-utils';

const UNSUPPORTED_ENTITY_TYPES: string[] = [];

/**
 * Select the transformer that should drive a form for `entityType`, constrained
 * to the granted flavor `versionName` when it is known.
 *
 * An entity type can have several flavors (e.g. JOB → `UserJobTransportOrder`
 * and `UserWasteContainerJob`). Matching on entity type alone would bind the
 * form to whichever flavor happens to come first in the transformer list, so a
 * dataspace granted only one flavor could edit/submit under the wrong one.
 * Passing `versionName` pins the selection to the granted flavor; `null` keeps
 * the legacy type-only behavior for callers without a flavor to enforce.
 *
 * Among the survivors the native (non-CSV) representation is preferred.
 */
export function selectEditionTransformer(
  transformers: TransformerInterface[],
  entityType: string,
  versionName: string | null
): TransformerInterface | null {
  if (UNSUPPORTED_ENTITY_TYPES.includes(entityType)) {
    return null;
  }

  const matching = transformers.filter(
    (t: TransformerInterface) =>
      t.transformerInfo.transformer.generatedEntities.length === 1 &&
      t.transformerInfo.transformer.generatedEntities.includes(entityType) &&
      t.transformerInfo.dataModels.length > 0 &&
      (versionName === null || t.transformerInfo.transformer.name === versionName)
  );
  if (matching.length === 1) {
    return matching[0];
  }

  const nonCsvMatching = matching.filter(
    (t: TransformerInterface) =>
      !isCsvCompatible(t.transformerInfo.dataModels[0].metadata.schema)
  );

  return nonCsvMatching[0] ?? null;
}

/**
 * The representations of `entityType` that can be exported as CSV, as the
 * `entityTypeVersion`s the export request names.
 *
 * A transformer qualifies only when *every* model it declares is flat: a mixed
 * transformer would put a nested model behind a CSV export it cannot serialise.
 * Input-only models are dropped individually — the data layer cannot rebuild them
 * from stored entities, so an export naming one fails by construction.
 */
export function csvExportModelNames(
  transformers: TransformerInterface[],
  entityType: string
): string[] {
  return transformers
    .filter(
      (t: TransformerInterface) =>
        t.transformerInfo.transformer.generatedEntities.includes(entityType) &&
        t.transformerInfo.dataModels.every((model) =>
          isCsvCompatible(model.metadata.schema)
        )
    )
    .flatMap((t: TransformerInterface) =>
      t.transformerInfo.dataModels
        .filter((model) => !isInputOnly(model.metadata.schema))
        .map((model) => model.metadata.entityTypeVersion)
    );
}
